import { useCallback, useState, type ReactNode } from "react";
import { User as UserIcon, UserPlus, Users } from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { usePermissao } from "@/lib/use-permissao";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { colunasMembro } from "@/lib/colunas-export";
import { getRoleLabel } from "@/components/team/role-meta";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { resolveDashboardMode, type DashboardMode } from "@/lib/dashboard-mode";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { TeamInviteDialog } from "@/components/team/TeamInviteDialog";
import { TeamInvitesList } from "@/components/team/TeamInvitesList";
import { TeamMembersList } from "@/components/team/TeamMembersList";
import { TeamSummaryChips } from "@/components/team/TeamSummaryChips";
import { TeamVisaoGeral } from "@/components/team/TeamVisaoGeral";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { EQUIPE_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { useIsMobile } from "@/lib/use-media-query";

const PAGE_SUBTITLE_BY_MODE = {
  office: "Acompanhe a operação e gerencie os membros da sua clínica",
  lab: "Acompanhe a operação e gerencie os membros do seu laboratório",
  hybrid: "Acompanhe a operação e gerencie os membros da sua organização",
} as const;

const CARD_SUBTITLE_BY_MODE = {
  office: "Dentistas, assistentes e demais membros",
  lab: "Técnicos, produção e demais membros",
  hybrid: "Membros da clínica e do laboratório",
} as const;

export function Team() {
  const { user } = useAuth();
  const mode = resolveDashboardMode(user?.activeOrganization?.types ?? []);
  const utils = trpc.useUtils();
  const podeGerenciar = usePermissao("team.manage");
  const isMobile = useIsMobile();

  // `team.list` não pagina — vem inteira. Ainda assim busca de novo em vez de reusar o cache: o
  // arquivo tem que refletir o que está no servidor agora, não uma lista que pode estar velha.
  const buscarEquipeParaExportar = useCallback(async () => (await utils.team.list.fetch()) as any[], [utils]);

  useTourDaPagina(EQUIPE_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Equipe",
      subtitle: PAGE_SUBTITLE_BY_MODE[mode],
    },
    [mode],
  );

  return (
    // `max-w-[1600px]`, a faixa das tabelas de dados (pacientes, estudos, USUÁRIOS): a Equipe é uma
    // dessas listas, e o cap de ficha que estava aqui deixava a tabela apertada em monitor largo.
    <div className="mx-auto w-full max-w-[1600px] space-y-4 pb-10">
      <TeamTabs
        mode={mode}
        exportar={
          podeGerenciar && (
            <BotaoExportar
              // No celular é ação rara demais pra botão por extenso ao lado do Convidar.
              gatilho={isMobile ? "tresPontos" : "botao"}
              nome="equipe"
              titulo="equipe"
              planilha="equipe"
              colunas={colunasMembro((role) => getRoleLabel(role as any, mode))}
              idDe={(m) => m.id}
              buscarLinhas={buscarEquipeParaExportar}
              className="h-11 shrink-0 bg-white text-[12.5px] font-semibold sm:h-8 dark:bg-card"
            />
          )
        }
      />
    </div>
  );
}

// ─── Abas ──────────────────────────────────────────────────

type Aba = "visao" | "membros";

/**
 * Duas leituras da mesma equipe: "Visão geral" acompanha a operação do dia, "Membros e acessos"
 * administra quem entra e o que pode.
 *
 * A Visão geral abre por padrão pra quem gerencia a equipe — é a pergunta do dia a dia ("como
 * estamos agora?"), enquanto administrar membro é episódico. Quem não gerencia cai direto em
 * "Membros e acessos", que é onde vive o bloqueio explicando a falta de acesso.
 */
function TeamTabs({ mode, exportar }: { mode: DashboardMode; exportar: ReactNode }) {
  const meQuery = trpc.me.current.useQuery();
  const isPersonal = meQuery.data?.activeOrganization?.isPersonal;
  const canManage = !isPersonal && !!meQuery.data?.activeOrganization?.canManageTeam;
  // Vindo da busca (`?abrir=`), cai direto na aba onde o colaborador se abre.
  const [aba, setAba] = useState<Aba>(() =>
    new URLSearchParams(window.location.search).has("abrir") ? "membros" : "visao",
  );
  const abaAtiva: Aba = canManage ? aba : "membros";

  return (
    <div
      data-tour="equipe-membros"
      // No celular a moldura vira faixa de largura total: com a margem da página mais a da moldura,
      // os cartões de dentro ficavam espremidos e o do profissional vazava.
      className="rounded-xl border border-zinc-200 bg-white shadow-sm max-md:-mx-4 max-md:rounded-none max-md:border-x-0 max-md:shadow-none dark:border-border dark:bg-card"
    >
      {/* Sem título interno: "Equipe" já é o título da página no topo, e repeti-lo aqui (como
          "Equipe hoje"/"Membros") dizia duas vezes a mesma coisa e ainda brigava com o título da
          seção logo abaixo. Sobram as abas e o Convidar — que fica nas duas, por ser a ação de
          equipe mais frequente. No celular abas e botões não cabem numa linha: os botões sobem
          (`flex-col-reverse`) pra que as abas continuem encostadas na borda de baixo. */}
      <div className="flex flex-col-reverse border-b border-zinc-100 px-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6 dark:border-border">
        {canManage ? (
          <div className="flex gap-1" role="tablist" aria-label="Seções da equipe">
            {(
              [
                { chave: "visao" as const, rotulo: "Visão geral" },
                { chave: "membros" as const, rotulo: "Membros e acessos" },
              ]
            ).map((t) => (
              <button
                key={t.chave}
                type="button"
                role="tab"
                aria-selected={abaAtiva === t.chave}
                onClick={() => setAba(t.chave)}
                className={cn(
                  // Seleção marcada SÓ por texto azul e linha inferior azul. O anel de foco fica em
                  // `focus-visible`, então aparece na navegação por teclado e não depois do clique.
                  "-mb-px rounded-none border-b-2 px-3 py-3 text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
                  abaAtiva === t.chave
                    ? "border-brand-600 font-semibold text-brand-700 dark:border-primary dark:text-primary"
                    : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {t.rotulo}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 py-4">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ backgroundColor: "#0ea5e91a", color: "#0ea5e9" }}
            >
              <Users className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Membros</h2>
              <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                {CARD_SUBTITLE_BY_MODE[mode]}
              </p>
            </div>
          </div>
        )}
        <div className="flex shrink-0 items-center justify-end gap-2 pt-3 empty:hidden sm:pt-0">
          {exportar}
          <InviteButton />
        </div>
      </div>

      {abaAtiva === "visao" ? <TeamVisaoGeral mode={mode} /> : <TeamBody />}
    </div>
  );
}

// ─── Convite + lista de membros ────────────────────────────

function InviteButton() {
  const [open, setOpen] = useState(false);
  const meQuery = trpc.me.current.useQuery();
  const isPersonal = meQuery.data?.activeOrganization?.isPersonal;
  const canManage = !isPersonal && !!meQuery.data?.activeOrganization?.canManageTeam;

  if (!canManage) return null;

  return (
    <>
      <button
        type="button"
        data-tour="equipe-convidar"
        onClick={() => setOpen(true)}
        className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 sm:h-8"
      >
        <UserPlus className="h-3.5 w-3.5" />
        Convidar
      </button>
      <TeamInviteDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function TeamBody() {
  const meQuery = trpc.me.current.useQuery();
  const isPersonal = meQuery.data?.activeOrganization?.isPersonal;
  const canManage = !isPersonal && !!meQuery.data?.activeOrganization?.canManageTeam;
  const membersQuery = trpc.team.list.useQuery(undefined, { enabled: canManage });
  const pendingInvitesQuery = trpc.team.invite.list.useQuery(
    { status: "PENDING" },
    { enabled: canManage },
  );

  if (isPersonal) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary">
          <UserIcon className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Esta é sua organização pessoal
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          Organizações pessoais não aceitam convites. Crie uma organização de
          equipe (em Organizações, no menu) pra convidar colegas.
        </p>
      </div>
    );
  }

  // Sem team.manage: nem convites pendentes nem a lista dos colegas aparecem — ver issue #9.
  if (!canManage) {
    return <NoAccessBlock description="A visualização da equipe é restrita a quem gerencia a organização." />;
  }

  return (
    <>
      <TeamSummaryChips
        members={membersQuery.data ?? []}
        pendingInvitesCount={pendingInvitesQuery.data?.length ?? 0}
      />
      <div className="px-6 py-4">
        <TeamInvitesList />
        <TeamMembersList canManage={canManage} />
      </div>
    </>
  );
}
